import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

export function discountDetails(variant) {
  if (!variant || !Number.isSafeInteger(variant.price) || variant.price < 0 || !Number.isSafeInteger(variant.compare_at_price) || variant.compare_at_price <= variant.price) return null;
  const saving = variant.compare_at_price - variant.price;
  return { saving, percent: Number(BigInt(saving) * 100n / BigInt(variant.compare_at_price)) };
}

export function tunicImage(productId, house, images) {
  if (productId !== '8754250743969' || !['PHOENIX', 'UNICORN', 'GRIFFIN', 'PTERELEON', 'PEGASUS'].includes(house)) return '';
  return images[house] || '';
}

export function accessoryImage(productId, color, images) {
  if (productId === '8335909945505') return images.neutral || '';
  if (productId === '8337631117473' && color === 'Black') return images.black || '';
  if ((productId === '8337631117473' || productId === '8337657856161') && color === 'White') return images.white || '';
  return '';
}

export function resolveVariant(data, values) {
  if (values.length !== data.options.length || values.some(value => !value)) return null;
  return data.variants.find(variant => variant.options.length === values.length && variant.options.every((value, index) => value === values[index])) || null;
}
export function quantityValid(variant, value, cartQuantity = 0) {
  const quantity = Number(value);
  return Boolean(variant && Number.isSafeInteger(quantity) && quantity >= variant.min && (quantity - variant.min) % variant.step === 0 && (variant.max == null || quantity + cartQuantity <= variant.max));
}
export function optionAvailable(data, values, index, value) {
  return data.variants.some(variant => variant.available && variant.options[index] === value && variant.options.every((candidate, position) => position === index || !values[position] || values[position] === candidate));
}
export function validateData(data, productId) {
  if (typeof data.special_purchase !== 'boolean' || typeof data.has_only_default_variant !== 'boolean' || String(data.product_id) !== String(productId) || !Array.isArray(data.variants) || data.variant_count !== data.variants.length || !data.variants.length || data.variants.length > 250 || !Array.isArray(data.options)) throw new Error('Incomplete variant data');
  data.options = data.options.map(option => ({ name: option.name, values: option.values.map(value => typeof value === 'string' ? value : value.name) }));
  const ids = new Set(), tuples = new Set();
  for (const variant of data.variants) {
    const tuple = JSON.stringify(variant.options);
    if (!/^\d+$/.test(variant.id) || ids.has(variant.id) || tuples.has(tuple) || typeof variant.available !== 'boolean' || !Array.isArray(variant.options) || variant.options.length !== data.options.length || !variant.options.every((value, index) => data.options[index].values.includes(value)) || !Number.isSafeInteger(variant.price) || variant.price < 0 || !Number.isSafeInteger(variant.min) || variant.min < 1 || !Number.isSafeInteger(variant.step) || variant.step < 1 || (variant.max != null && (!Number.isSafeInteger(variant.max) || variant.max < 1))) throw new Error('Invalid variant data');
    ids.add(variant.id); tuples.add(tuple);
  }
  return data;
}

class CnsQuickOrder extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.messages = JSON.parse(this.querySelector('[data-translations]').textContent);
    this.accessoryImages = JSON.parse(this.querySelector('[data-accessory-images]')?.textContent || '{}');
    this.tunicImages = JSON.parse(this.querySelector('[data-tunic-images]')?.textContent || '{}');
    this.formComponent = this.querySelector('product-form-component');
    this.form = this.formComponent.querySelector('form');
    this.variantInput = this.form.querySelector('[name="id"]');
    this.addButton = this.form.querySelector('[type="submit"]');
    this.quantityInput = this.form.querySelector('[name="quantity"]');
    this.quantityComponent = this.form.querySelector('quantity-selector-component');
    this.status = this.querySelector('[data-option-status]');
    this.busy = this.busy || false;
    this.initialImage = this.initialImage || this.querySelector('[data-quick-image]')?.getAttribute('src') || '';
    const options = { signal: this.controller.signal };
    this.querySelector('[data-focus-options]').disabled = false;
    this.querySelector('[data-focus-options]').addEventListener('click', async () => { await this.load(); this.focusOptions(); }, options);
    this.querySelector('[data-retry]').addEventListener('click', () => this.load(), options);
    this.addEventListener('focusin', () => this.load(), options);
    this.form.addEventListener('submit', event => this.guardSubmit(event), { ...options, capture: true });
    this.quantityInput.addEventListener('input', () => this.refreshSelection(false), options);
    this.quantityInput.addEventListener('change', () => this.refreshSelection(false), options);
    this.quantityComponent.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.refreshSelection(false), options);
    this.addEventListener(StandardEvents.cartLinesUpdate, event => this.onOwnCartEvent(event), options);
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (event.target === this.formComponent) return;
      event.promise?.then(({ detail }) => {
        if (!this.isConnected || !this.data || !detail?.items) return;
        this.applyCart(detail);
        this.refreshSelection(false);
      }).catch(() => {});
    }, options);
    if (this.data) {
      const saved = this.selectedValues(); this.buildOptions(saved); this.refreshSelection(false);
    } else if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); } }, { rootMargin: '150px' });
      this.observer.observe(this);
    } else this.load();
    this.watchCardMarkup();
    const controller = this.controller;
    queueMicrotask(() => {
      if (!this.isConnected || this.controller !== controller) return;
      this.quantityComponent.serverDisabledMinus = false;
      this.quantityComponent.serverDisabledPlus = false;
      if (this.busy) this.form.setAttribute('aria-busy', 'true'); else this.form.removeAttribute('aria-busy');
      if (this.data) this.refreshSelection(false);
    });
  }
  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.helperObserver?.disconnect(); this.controller = null; this.loading = null; }
  watchCardMarkup() {
    const controller = this.controller;
    const refresh = () => {
      if (this.markupRefreshQueued) return;
      this.markupRefreshQueued = true;
      queueMicrotask(() => {
        this.markupRefreshQueued = false;
        if (!this.isConnected || this.controller !== controller) return;
        const formComponent = this.querySelector('product-form-component');
        const form = formComponent?.querySelector('form');
        if (!form || !this.querySelector('[data-options]') || !this.querySelector('[data-focus-options]')) return;
        const stale = this.formComponent !== formComponent || this.form !== form || this.variantInput !== form.querySelector('[name="id"]') || (this.data && this.selects?.some(select => !this.contains(select))) || this.querySelector('[data-focus-options]').disabled;
        if (stale) {
          this.disconnectedCallback();
          this.connectedCallback();
          return;
        }
        if (customElements.get('s24-size-helper') && typeof HTMLDialogElement !== 'undefined' && HTMLDialogElement.prototype.showModal) this.repairSizeHelper();
      });
    };
    this.helperObserver?.disconnect();
    this.helperObserver = new MutationObserver(refresh);
    this.helperObserver.observe(this, { childList: true, subtree: true });
    refresh();
    customElements.whenDefined('s24-size-helper').then(refresh);
  }
  repairSizeHelper() {
    const helper = this.querySelector('s24-size-helper');
    if (!helper || helper.querySelector('dialog.s24sh__dialog') || !helper.querySelector('[data-launch]')?.hidden || !helper.querySelector('[data-fallback] .s24sh__panel')) return;
    // Native section morphing can retain an initialized custom element while
    // replacing its children with server markup. Replace only that broken card
    // helper, allowing its existing custom-element implementation to initialize.
    helper.replaceWith(helper.cloneNode(true));
  }
  async load() {
    if (this.data) return;
    if (this.loading) return this.loading;
    const signal = this.controller.signal;
    this.querySelector('[data-retry]').hidden = true;
    this.status.textContent = this.messages.loading;
    const url = new URL(this.dataset.productUrl, window.location.href);
    url.search = '';
    url.searchParams.set('view', 'size24-cns-quickorder');
    this.loading = (async () => {
      try {
        let merged = null;
        const visited = new Set();
        for (let page = 0; page < 5; page++) {
          if (visited.has(url.toString())) throw new Error('Repeated variant page');
          visited.add(url.toString());
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal });
          if (!response.ok) throw new Error('Options request failed');
          const part = await response.json();
          if (!Array.isArray(part.variants) || part.variant_count > 250 || String(part.product_id) !== this.dataset.productId) throw new Error('Invalid variant page');
          if (!merged) merged = part;
          else {
            if (merged.variant_count !== part.variant_count || merged.special_purchase !== part.special_purchase || merged.has_only_default_variant !== part.has_only_default_variant || JSON.stringify(merged.options) !== JSON.stringify(part.options)) throw new Error('Product changed while loading');
            merged.variants.push(...part.variants);
          }
          if (part.next_page == null) break;
          if (!Number.isSafeInteger(part.next_page) || !/^page(?:_[a-zA-Z0-9]+)?$/.test(part.page_param)) throw new Error('Invalid pagination');
          url.searchParams.set(part.page_param, String(part.next_page));
        }
        const data = validateData(merged, this.dataset.productId);
        await Promise.all([customElements.whenDefined('product-form-component'), customElements.whenDefined('quantity-selector-component'), customElements.whenDefined('add-to-cart-component')]);
        if (signal.aborted || !this.isConnected) return;
        this.data = data;
        this.buildOptions();
        this.refreshSelection(true);
      } catch (error) {
        if (signal.aborted) return;
        this.status.textContent = this.messages.load_error;
        this.querySelector('[data-retry]').hidden = false;
      } finally { if (!signal.aborted) this.loading = null; }
    })();
    return this.loading;
  }
  focusOptions() {
    const group = this.selects?.[0];
    (group?.options.find(input => input.checked && !input.disabled) || group?.options.find(input => !input.disabled))?.focus();
  }
  buildOptions(saved = []) {
    const container = this.querySelector('[data-options]');
    container.replaceChildren();
    this.selects = this.data.options.map((option, index) => {
      const group = document.createElement('fieldset'), legend = document.createElement('legend'), choices = document.createElement('div');
      const id = this.formComponent.id + '-option-' + index;
      group.className = 's24qo__option-group'; group.id = id;
      legend.textContent = option.name; choices.className = 's24qo__choices';
      group.value = '';
      if (saved[index] && option.values.includes(saved[index])) group.value = saved[index];
      else if (this.data.has_only_default_variant) group.value = this.data.variants[0].options[index];
      else if (option.name.toLowerCase() === 'color' && Number(this.dataset.filterCount) === 1 && option.values.includes(this.dataset.filterHouse)) group.value = this.dataset.filterHouse;
      group.options = option.values.map((value, valueIndex) => {
        const input = document.createElement('input'), label = document.createElement('label'), text = document.createElement('span');
        input.type = 'radio'; input.className = 's24qo__radio'; input.id = id + '-value-' + valueIndex; input.name = id;
        input.setAttribute('form', id + '-not-submitted'); input.value = value; input.checked = group.value === value;
        label.htmlFor = input.id; label.className = 's24qo__choice'; text.textContent = value;
        if (option.name.toLowerCase() === 'color') {
          const image = this.data.variants.find(variant => variant.options[index] === value && variant.image)?.image;
          if (image) { const swatch = document.createElement('img'); swatch.src = image; swatch.alt = ''; swatch.loading = 'lazy'; swatch.width = 32; swatch.height = 32; swatch.className = 's24qo__swatch'; label.append(swatch); }
        }
        label.append(text); choices.append(input, label);
        input.addEventListener('change', () => { if (input.checked) { group.value = value; this.refreshSelection(true); } }, { signal: this.controller.signal });
        return input;
      });
      const clear = document.createElement('button'); clear.type = 'button'; clear.className = 's24qo__clear'; clear.textContent = this.messages.clear;
      clear.setAttribute('aria-label', this.messages.clear_option.replace('__OPTION__', option.name));
      clear.addEventListener('click', () => { group.value = ''; group.options.forEach(input => { input.checked = false; }); this.refreshSelection(true); (group.options.find(input => !input.disabled))?.focus(); }, { signal: this.controller.signal });
      group.clearButton = clear; group.append(legend, choices, clear); container.append(group); return group;
    });
    container.hidden = Boolean(this.data.has_only_default_variant);
    this.querySelector('[data-quantity-wrap]').hidden = false;
  }
  selectedValues() { return this.selects?.map(select => select.value) || []; }
  refreshSelection(changed) {
    if (!this.data) return;
    const values = this.selectedValues(), variant = resolveVariant(this.data, values);
    this.variant = variant;
    const supported = !this.data.special_purchase;
    this.variantInput.value = variant?.id || '';
    this.variantInput.disabled = !variant?.available || !supported;
    for (const [index, select] of this.selects.entries()) {
      select.disabled = this.busy || !supported;
      for (const option of select.options) { option.checked = option.value === select.value; option.disabled = this.busy || !supported || !optionAvailable(this.data, values, index, option.value); }
      select.clearButton.disabled = this.busy || !supported || !select.value;
    }
    if (variant) {
      const quantity = this.quantityInput;
      quantity.min = variant.min; quantity.step = variant.step; quantity.dataset.min = variant.min;
      if (variant.max != null) quantity.max = variant.max; else quantity.removeAttribute('max');
      quantity.dataset.cartQuantity = variant.cart_quantity || 0;
      this.quantityComponent.dataset.variantId = variant.id;
      if (changed && !quantityValid(variant, quantity.value, 0) && !(this.quantityComponent.zeroStart && Number(quantity.value) === 0)) quantity.value = variant.min;
      this.quantityComponent.updateButtonStates?.();
      this.formComponent.dataset.quantityDefault = variant.min;
      this.querySelector('[data-quantity-rules]').textContent = this.messages.rules.replace('{min}', variant.min).replace('{step}', variant.step) + (variant.max == null ? '' : ' ' + this.messages.max.replace('{max}', variant.max));
      this.querySelector('[data-price]').textContent = variant.price_text;
      const discount = discountDetails(variant);
      const compare = this.querySelector('[data-compare]'); compare.hidden = !discount; compare.textContent = discount ? variant.compare_text || '' : '';
      const compareWrap = this.querySelector('[data-compare-wrap]'); if (compareWrap) compareWrap.hidden = !discount;
      const badge = this.querySelector('[data-discount]');
      if (badge) {
        badge.hidden = !discount || typeof variant.saving_text !== 'string' || !variant.saving_text;
        badge.textContent = badge.hidden ? '' : this.messages.discount.replace('__PERCENT__', discount.percent ? String(discount.percent) : this.messages.under_one).replace('__AMOUNT__', variant.saving_text);
      }
      const unit = this.querySelector('[data-unit]'); unit.hidden = !variant.unit_price_text; unit.textContent = variant.unit_price_text ? variant.unit_price_text + ' / ' + variant.unit_reference : '';
      const details = new URL(this.dataset.productUrl, window.location.href); details.searchParams.set('variant', variant.id); this.querySelector('[data-product-details]').href = details.toString();
    }
    this.querySelector('[data-initial-price]').hidden = Boolean(variant);
    this.querySelector('[data-selected-price]').hidden = !variant;
    if (!variant) { const badge = this.querySelector('[data-discount]'); if (badge) { badge.hidden = true; badge.textContent = ''; } }
    const valid = Boolean(supported && variant?.available && quantityValid(variant, this.quantityInput.value, variant.cart_quantity || 0));
    this.addButton.disabled = this.busy || !valid;
    this.quantityInput.disabled = this.busy || !variant?.available || !supported;
    this.quantityComponent.updateButtonStates?.();
    if (this.quantityInput.disabled) for (const button of this.quantityComponent.querySelectorAll('button')) button.disabled = true;
    this.querySelector('[data-add-label]').textContent = this.busy ? this.messages.adding : this.messages.add;
    if (!this.busy) this.status.textContent = !supported ? this.messages.special : !variant ? (values.some(value => !value) ? this.messages.choose : this.messages.unavailable) : !variant.available ? this.messages.unavailable : !valid ? this.messages.quantity_error : '';
    this.updateImage(values, variant);
  }
  updateImage(values, variant) {
    const colorIndex = this.data.options.findIndex(option => option.name.toLowerCase() === 'color');
    const color = colorIndex < 0 ? '' : values[colorIndex];
    const blueContext = colorIndex < 0 || !color || color === 'PEGASUS';
    const filterAllows = Number(this.dataset.filterCount) === 0 || (Number(this.dataset.filterCount) === 1 && this.dataset.filterHouse === 'PEGASUS');
    const useArt = Boolean(this.dataset.aiImage && blueContext && filterAllows);
    const representative = variant || (color ? this.data.variants.find(item => item.options[colorIndex] === color && item.image) : null);
    const accessoryColor = color || this.accessoryImages?.initialColor || '';
    const accessory = accessoryImage(this.dataset.productId, accessoryColor, this.accessoryImages || {});
    const tunicHouse = color || (Number(this.dataset.filterCount) === 1 ? this.dataset.filterHouse : this.tunicImages?.initialColor || '');
    const tunicAllowed = Number(this.dataset.filterCount) === 0 || (Number(this.dataset.filterCount) === 1 && this.dataset.filterHouse === tunicHouse);
    const tunic = tunicAllowed ? tunicImage(this.dataset.productId, tunicHouse, this.tunicImages || {}) : '';
    const url = tunic || accessory || (useArt ? this.dataset.aiImage : representative?.image || ((!color || colorIndex < 0) ? this.initialImage : ''));
    let image = this.querySelector('[data-quick-image]');
    if (url && !image) { image = document.createElement('img'); image.alt = ''; image.loading = 'lazy'; image.dataset.quickImage = ''; this.querySelector('[data-focus-options]').prepend(image); }
    if (image) { image.hidden = !url; if (url && image.getAttribute('src') !== url) { image.removeAttribute('srcset'); image.removeAttribute('sizes'); image.src = url; } }
    this.querySelector('[data-missing-image]').hidden = Boolean(url);
    const note = this.querySelector('[data-image-note]'); note.hidden = !tunic && !useArt && !accessory; note.textContent = tunic ? this.tunicImages.caption.replace('__HOUSE__', tunicHouse) : accessory ? (accessoryColor ? this.messages.accessory_color_caption.replace('__COLOR__', accessoryColor) : this.messages.accessory_caption) : (useArt ? this.dataset.aiCaption : '');
  }
  guardSubmit(event) {
    const variant = this.data && resolveVariant(this.data, this.selectedValues());
    if (this.busy || !variant?.available || this.data.special_purchase || !quantityValid(variant, this.quantityInput.value, variant.cart_quantity || 0) || this.variantInput.disabled || this.variantInput.value !== variant.id || !this.form.checkValidity()) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!this.busy) { this.refreshSelection(false); this.form.reportValidity(); }
    }
  }
  applyCart(detail) {
    if (!this.data || !detail?.items) return;
    for (const variant of this.data.variants) variant.cart_quantity = detail.items.filter(item => String(item.variant_id) === variant.id).reduce((total, item) => total + item.quantity, 0);
  }
  onOwnCartEvent(event) {
    if (event.target !== this.formComponent || event.action !== 'add' || !event.promise) return;
    this.busy = true; this.form.setAttribute('aria-busy', 'true'); this.refreshSelection(false);
    event.promise.then(({ detail }) => {
      this.busy = false; this.applyCart(detail);
      if (!this.isConnected) return; this.form.removeAttribute('aria-busy'); this.refreshSelection(false);
      this.status.textContent = detail?.didError ? this.messages.cart_error : this.messages.added;
    }).catch(() => {
      this.busy = false;
      if (!this.isConnected) return; this.form.removeAttribute('aria-busy'); this.refreshSelection(false); this.status.textContent = this.messages.cart_error;
    });
  }
}
if (!customElements.get('s24-cns-quickorder')) customElements.define('s24-cns-quickorder', CnsQuickOrder);
